import { client } from "venuescanner-js/services/socketClient";
import consume from "./models/consume";

const thread = ({ inboxService, states }) => {
  /**
   * @typedef {Object} threadCollection - Object containing the associated thread models
   * @property {Object} thread - Thread data according to the inbox schema
   * @property {Object} admin_thread_info - Admin thread info data according to the inbox schema
   * @property {Object} admin_event - Admin event data according to the inbox schema
   * @property {Object} space - Space data according to the inbox schema
   * @property {Object} venue - Venue data according to the inbox schema
   * @property {Object} host - Host data according to the inbox schema
   * @property {Object} organiser - Organiser data according to the inbox schema
   */
  return {
    /** @function
     * @async
     * @name thread.fetch
     * @desc Fetch a thread and associated models
     * @memberof! module:inboxService
     * @param {String} _id - The _id of the thread
     * @returns {threadCollection}
     */
    fetch: async function (_id) {
      const objects = await client.sendPromise("room", {
        "thread.get": { _id },
      });
      Object.keys(objects).forEach((modelName) => {
        objects[modelName] = consume(modelName, objects[modelName]);
      });

      // if (thread) {
      //   states.loaded.thread = [...new Set([...states.loaded.thread,
      //     thread._id
      //   ])]
      // }
      // if (admin_thread_info) {
      //   states.loaded.admin_thread_info = [...new Set([...states.loaded.admin_thread_info,
      //     admin_thread_info._id
      //   ])]
      // }
      // if (admin_event) {
      //   states.loaded.admin_event = [...new Set([...states.loaded.admin_event,
      //     admin_event._id
      //   ])]
      // }
      // inboxService.set.loaded(states.loaded)
      return objects;
    },

    /** @function
     * @async
     * @name thread.reply
     * @desc Reply to a thread
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @param {String} options.message - The message to reply with
     * @param {String[]} options.attachments - Any attachments to include in the response
     * @returns {Boolean} true is successful
     */
    reply: async function ({ _id, message, attachments }) {
      return client.sendPromise("room", {
        "thread.reply": { _id, message, attachments },
      });
    },

    /** @function
     * @async
     * @name thread.update
     * @desc Update to a thread
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @param {String} options.message - The message to reply with
     * @param {String[]} options.attachments - Any attachments to include in the response
     * @param {Object} options.offer - Ammended offer details
     * @param {Object} options.payment - Payment amount for initial deposit
     * @param {Object} options.schedule - Deposit schedule for future payments
     * @returns {Boolean} true is successful
     */
    update: async function ({
      _id,
      message,
      attachments,
      offer,
      payment,
      schedule,
    }) {
      return client.sendPromise("room", {
        "thread.update": {
          _id,
          message,
          attachments,
          offer,
          payment,
          schedule,
        },
      });
    },

    /** @function
     * @async
     * @name thread.accept
     * @desc Reply & accept a thread
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @param {String} options.message - The message to reply with
     * @param {String[]} options.attachments - Any attachments to include in the response
     * @param {Object} options.offer - Accepted offer details as a diff
     * @param {Object} options.payment - Payment amount for initial deposit
     * @param {Object} options.schedule - Deposit schedule for future payments
     * @returns {Boolean} true is successful
     */
    accept: async function ({
      _id,
      message,
      attachments,
      offer,
      payment,
      schedule,
    }) {
      return client.sendPromise("room", {
        "thread.accept": {
          _id,
          message,
          attachments,
          offer,
          payment,
          schedule,
        },
      });
    },

    /** @function
     * @async
     * @name thread.book
     * @desc Book a thread
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @param {String} options.message - The message to reply with
     * @returns {Boolean} true is successful
     */
    book: async function ({ _id, message, attachments }) {
      return client.sendPromise("room", {
        "thread.book": { _id, message, attachments },
      });
    },
    trackContactDetailsReveal: async function ({ _id, field }) {
      return client.sendPromise("room", {
        "thread.trackContactDetailsReveal": { _id, field },
      });
    },

    /** @function
     * @async
     * @name thread.moveToFolder
     * @desc Place a thread in a folder
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @param {String} options.folderId - TThe _id of the folder
     * @returns {Boolean} true is successful
     */
    moveToFolder: async function ({ _id, folderId }) {},

    /** @function
     * @async
     * @name thread.markAsBooked
     * @desc Mark the thread as Booked
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @returns {Boolean} true is successful
     */
    markAsBooked: async function ({ _id, message, attachments }) {
      return client.sendPromise("room", {
        "thread.markAsBooked": { _id, message, attachments },
      });
    },

    /** @function
     * @async
     * @name thread.unmarkAsBooked
     * @desc Unmark the thread as Booked
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @returns {Boolean} true is successful
     */
    unmarkAsBooked: async function ({ _id }) {
      return client.sendPromise("room", {
        "thread.unmarkAsBooked": { _id },
      });
    },

    /** @function
     * @async
     * @name thread.markAsClosed
     * @desc Mark the thread as Closed
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @returns {Boolean} true is successful
     */
    markAsClosed: async function ({ _id, message, attachments }) {
      return client.sendPromise("room", {
        "thread.markAsClosed": { _id, message, attachments },
      });
    },

    /** @function
     * @async
     * @name thread.cancel
     * @desc Cancel the thread
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @returns {Boolean} true is successful
     */
    cancel: async function ({ _id, message, attachments, reason }) {
      return client.sendPromise("room", {
        "thread.cancel": { _id, message, attachments, reason },
      });
    },

    /** @function
     * @async
     * @name thread.decline
     * @desc Decline the thread
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @returns {Boolean} true is successful
     */
    decline: async function ({ _id, message, attachments, reason }) {
      return client.sendPromise("room", {
        "thread.decline": { _id, message, attachments, reason },
      });
    },

    /** @function
     * @async
     * @name thread.reopen
     * @desc Update to a thread
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @param {String} options.message - The message to reply with
     * @param {String[]} options.attachments - Any attachments to include in the response
     * @param {Object} options.quote - Ammended offer details
     * @returns {Boolean} true is successful
     */
    reopen: async function ({ _id, message, attachments, offer }) {
      return client.sendPromise("room", {
        "thread.reopen": { _id, message, attachments, offer },
      });
    },
    /** @function
     * @async
     * @name thread.requestPayment
     * @desc Request a payment on a booked thread
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @param {String} options.message - The message to reply with
     * @param {String} options.payment - The value of the payment requested
     * @param {String[]} options.attachments - Any attachments to include in the response
     * @returns {Boolean} true is successful
     */
    requestPayment: async function ({ _id, message, attachments, payment }) {
      return client.sendPromise("room", {
        "thread.requestPayment": { _id, message, attachments, payment },
      });
    },

    /** @function
     * @async
     * @name thread.showToOrganiser
     * @desc Let the Organiser see this thread - for Concierge managed only
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @returns {Boolean} true is successful
     */
    showToOrganiser: async function ({ _id }) {
      return client.sendPromise("room", {
        "thread.showToOrganiser": { _id },
      });
    },

    /** @function
     * @async
     * @name thread.nudgeHost
     * @desc Let the Organiser nudge the host that they are interested in booking
     * @memberof! module:inboxService
     * @param {Object} options
     * @param {String} options._id - The _id of the thread
     * @returns {Boolean} true is successful
     */
    nudgeHost: async function ({ _id, message, attachments }) {
      return client.sendPromise("room", {
        "thread.nudgeHost": { _id, message, attachments },
      });
    },
  };
};
export default thread;
